import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  Lock as PrivateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Space, CreateSpaceDto } from '@risk-management/shared';
import { spacesService } from '../services/spaces.service';

export const SpaceManagement: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if we should open create dialog from URL (only on initial render)
  const shouldOpenCreate = searchParams.get('action') === 'create';

  const [dialogOpen, setDialogOpen] = useState(shouldOpenCreate);
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);
  const [formData, setFormData] = useState<CreateSpaceDto>({
    name: '',
    description: '',
    isPublic: false,
  });

  // Clear the search param after initial render if it was set
  useEffect(() => {
    if (shouldOpenCreate) {
      setSearchParams({}, { replace: true });
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch accessible spaces (public + user access + team access)
  const { data: spaces, isLoading } = useQuery<Space[]>({
    queryKey: ['spaces', 'accessible'],
    queryFn: () => spacesService.getAccessible(),
  });

  const createMutation = useMutation({
    mutationFn: spacesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateSpaceDto }) =>
      spacesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: spacesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });

  const handleOpenDialog = (space?: Space) => {
    if (space) {
      setEditingSpace(space);
      setFormData({
        name: space.name,
        description: space.description || '',
        isPublic: space.isPublic,
      });
    } else {
      setEditingSpace(null);
      setFormData({
        name: '',
        description: '',
        isPublic: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSpace(null);
  };

  const handleSubmit = () => {
    if (editingSpace) {
      updateMutation.mutate({ id: editingSpace.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((globalThis as any).confirm(t('space.deleteConfirm'))) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('nav.spaces')}
        </Typography>
        <Button variant="contained" onClick={() => handleOpenDialog()}>
          {t('space.create')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('space.name')}</TableCell>
              <TableCell>{t('space.description')}</TableCell>
              <TableCell>{t('space.isPublic')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {spaces?.map((space: Space) => (
              <TableRow
                key={space.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/spaces/${space.id}`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {space.isPublic ? (
                      <PublicIcon fontSize="small" color="action" />
                    ) : (
                      <PrivateIcon fontSize="small" color="action" />
                    )}
                    {space.name}
                  </Box>
                </TableCell>
                <TableCell>{space.description}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      space.isPublic ? t('space.public') : t('space.private')
                    }
                    color={space.isPublic ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <IconButton
                    onClick={() => handleOpenDialog(space)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(space.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!spaces || spaces.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {t('space.noSpaces')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSpace ? t('space.edit') : t('space.create')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('space.name')}
            fullWidth
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label={t('space.description')}
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={e =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={e =>
                  setFormData({ ...formData, isPublic: e.target.checked })
                }
              />
            }
            label={t('space.isPublic')}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.name ||
              createMutation.isPending ||
              updateMutation.isPending
            }
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
